import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { QueryClient } from '@tanstack/react-query';
import { configureSellApi } from '@services/axiosInstance';
import { setMapsApiKey, resetMapsApiKey } from '@config/mapsConfig';

// ---------------------------------------------------------------------------
// Single-instance enforcement
//
// SELL_API is a shared module-level singleton: if two SellConfigProviders
// mount concurrently with different tenants, the last to mount overwrites
// baseURL and X-Tenant-Id for all requests. Per-instance Axios is tracked
// as a follow-up. For now we enforce single-instance-per-page and warn loudly
// in development when this contract is violated.
// ---------------------------------------------------------------------------
let _activeInstances = 0;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

type SellConfigValue = {
  /** Per-instance QueryClient — each SellConfigProvider mount gets its own cache. */
  queryClient: QueryClient;
};

const SellConfigContext = createContext<SellConfigValue | null>(null);

export const useSellConfig = (): SellConfigValue => {
  const ctx = useContext(SellConfigContext);
  if (!ctx) throw new Error('useSellConfig must be used within SellConfigProvider');
  return ctx;
};

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

const MissingConfigError = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
    <Typography color="error">
      CPQ configuration error: <strong>apiBaseUrl</strong> is required in embedded mode. Pass it as a
      prop to <strong>EntryRouter</strong>.
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

type SellConfigProviderProps = {
  apiBaseUrl: string | undefined;
  tenantId: string | undefined;
  mapsApiKey: string | undefined;
  children: ReactNode;
};

export const SellConfigProvider = ({
  apiBaseUrl,
  tenantId,
  mapsApiKey,
  children,
}: SellConfigProviderProps): JSX.Element => {
  const effectiveUrl = apiBaseUrl ?? (import.meta.env.VITE_SELL_BASE_URL as string | undefined);

  // Per-instance QueryClient — created once per mount via useRef so it is
  // not shared with other SellConfigProvider instances or across remounts.
  const queryClientRef = useRef<QueryClient | null>(null);
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient();
  }

  // Apply runtime config synchronously before the subtree mounts so that
  // the first query fired by any child already has baseURL + tenant header.
  const configApplied = useRef(false);
  if (!configApplied.current) {
    configApplied.current = true;
    configureSellApi({ apiBaseUrl: effectiveUrl, tenantId });
    if (mapsApiKey) setMapsApiKey(mapsApiKey);
  }

  // Dev-mode guard: warn when two instances are active simultaneously.
  useEffect(() => {
    _activeInstances += 1;
    if (import.meta.env.DEV && _activeInstances > 1) {
      console.warn(
        '[cpq-mfe] Multiple EntryRouter instances detected on the same page. ' +
          'SELL_API is a shared singleton — concurrent mounts with different tenants overwrite ' +
          "each other's baseURL and X-Tenant-Id. Mount only one EntryRouter per page.",
      );
    }
    return () => {
      _activeInstances -= 1;
      resetMapsApiKey();
    };
  }, []);

  if (!effectiveUrl) return <MissingConfigError />;

  return (
    <SellConfigContext.Provider value={{ queryClient: queryClientRef.current }}>
      {children}
    </SellConfigContext.Provider>
  );
};
